import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api, type Id } from "../lib/api";
import { caughtErrorMessage } from "../domain/errors";
import { Icon } from "./ui";

export function PolicyAlertRecipients({
  companyId,
  policyId,
}: {
  readonly companyId: Id<"companies">;
  readonly policyId: Id<"companyPolicies">;
}) {
  const data = useQuery(api.policies.policyRecipients, { companyId, policyId });
  const setRecipient = useMutation(api.policies.setPolicyRecipient);
  const [busyUserId, setBusyUserId] = useState<Id<"users"> | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (data === undefined) {
    return <div className="rounded-lg bg-white p-4 text-[13px] text-slate-400">Loading staff assignments…</div>;
  }
  if (data === null) return null;

  const activeIds = new Set(data.assignments.filter((row) => row.active).map((row) => row.userId));
  const activeUsers = data.users.filter((user) => activeIds.has(user._id));
  const owner = data.policy.ownerName.trim().toLocaleLowerCase();

  const updateRecipient = async (userId: Id<"users">, active: boolean) => {
    setBusyUserId(userId);
    setError(null);
    try {
      await setRecipient({ companyId, policyId, userId, active });
    } catch (caught) {
      setError(caughtErrorMessage(caught, "Could not update alert recipients. Try again."));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <section
      aria-labelledby="policy-alerts-heading"
      className="rounded-lg bg-white p-4"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-dark)]">
          <Icon name="bell" size={16} />
        </span>
        <div>
          <h3 id="policy-alerts-heading" className="text-[16px] font-semibold text-slate-900">Assign staff to monitor this document</h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">Select the team members who should be emailed when a monitored change affects this document.</p>
        </div>
      </div>

      <div className="mt-3 grid gap-2">
        {data.users.length === 0 ? (
          <p className="rounded bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800">
            Add an active team member before assigning document alerts. <Link to="/team" className="font-semibold underline">Manage team</Link>
          </p>
        ) : data.users.map((user) => {
          const suggested = owner !== "" && [(user.name ?? "").trim().toLocaleLowerCase(), user.email.trim().toLocaleLowerCase()].includes(owner);
          return (
            <label key={user._id} className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 hover:bg-slate-50">
              <input
                type="checkbox"
                className="mt-0.5 accent-[var(--accent)]"
                checked={activeIds.has(user._id)}
                disabled={busyUserId !== null}
                onChange={(event) => void updateRecipient(user._id, event.target.checked)}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-slate-800">{user.name ?? user.email}</span>
                <span className="block truncate text-[11.5px] text-slate-400">{user.email}</span>
              </span>
              {suggested && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-sky-700">Document owner</span>}
            </label>
          );
        })}
      </div>

      {error !== null && <p className="mt-2 rounded bg-rose-50 px-3 py-2 text-[12px] text-rose-700" role="alert">{error}</p>}

      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-slate-100">
        <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Email preview</div>
        <div className="mt-1 text-[12.5px] font-semibold text-slate-800">Lawstream update — {data.policy.title}</div>
        <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
          The email identifies the monitored change, explains why this document is affected, and links to the relevant mapping section for review.
        </p>
      </div>

      {activeUsers.length > 0 ? (
        <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-emerald-800" role="status">
          <strong>Email alerts active.</strong> {activeUsers.map((user) => user.name ?? user.email).join(", ")} will receive relevant document updates. You can leave Lawstream—we’ll email them when something changes.
        </div>
      ) : !data.explicitConfigurationEnabled ? (
        <div className="mt-3 rounded-lg bg-sky-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-sky-800">
          No document-specific recipients yet. Until assignments are configured, update emails use the workspace’s active team-member list.
        </div>
      ) : (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-800">
          No one is assigned to this document. Select at least one team member to enable document-specific email alerts.
        </div>
      )}
    </section>
  );
}

export function PolicyAlertRecipientsModal({
  companyId,
  policyId,
  open,
  onClose,
}: {
  readonly companyId: Id<"companies">;
  readonly policyId: Id<"companyPolicies">;
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => {
      const firstCheckbox = dialogRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]:not(:disabled)');
      (firstCheckbox ?? closeButtonRef.current)?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  if (!open) return null;

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
    ) ?? [])].filter((element) => !element.hasAttribute("hidden"));
    if (focusable.length === 0) {
      event.preventDefault();
      closeButtonRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:items-center" role="presentation">
      <button type="button" tabIndex={-1} aria-label="Close staff assignment" className="absolute inset-0 bg-slate-950/45 backdrop-blur-[1px]" onClick={onClose} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="policy-alerts-heading" onKeyDown={trapFocus} className="relative z-10 w-full max-w-[34rem] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-900/10">
        <div className="flex justify-end border-b border-slate-100 px-4 py-2.5">
          <button ref={closeButtonRef} type="button" onClick={onClose} className="rounded px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800">Close</button>
        </div>
        <div className="max-h-[min(78vh,44rem)] overflow-y-auto">
          <PolicyAlertRecipients companyId={companyId} policyId={policyId} />
        </div>
      </div>
    </div>
  );
}
